/**
 * Job Offer Comparison Engine
 *
 * Pure calculation layer. Ports the math from a reference compensation model
 * that was specialized to three archetypes (options-heavy pre-IPO; late-stage
 * private RSU; late-stage RSU with IPO). Here those three code paths are
 * collapsed into one `buildOpportunity` function that dispatches on feature
 * flags carried by the Opportunity record.
 *
 * Tax engine numbers (NYC single filer, 2026) are preserved verbatim from the
 * reference as one preset; other states/filing statuses are provided as
 * additional presets or fully custom.
 */

// ═══════════════════════════════════════════════════════════════════
// SHARED EQUITY PRIMITIVES
// Instrument-agnostic tax/vesting/pool/format helpers now live in
// `equity-core.ts` so the standalone equity portfolio calculator can reuse
// the same math. They are re-exported here verbatim to preserve this engine's
// public surface — every existing import keeps resolving unchanged.
// ═══════════════════════════════════════════════════════════════════

import {
  buildVestSchedule,
  fmt,
  fmtN,
  pctFmt,
  poolSell,
  poolTotal,
  resolveTaxRates,
  ISO_100K_LIMIT,
  QSBS_CAP,
  TAX_PRESETS,
  TAX_PRESET_LABELS,
  type FilingStatus,
  type PoolLot,
  type RsuVestFrequency,
  type TaxPresetKey,
  type TaxRates
} from './equity-core.ts';

export {
  buildVestSchedule,
  fmt,
  fmtN,
  pctFmt,
  poolSell,
  poolTotal,
  resolveTaxRates,
  ISO_100K_LIMIT,
  QSBS_CAP,
  TAX_PRESETS,
  TAX_PRESET_LABELS
};
export type { FilingStatus, PoolLot, RsuVestFrequency, TaxPresetKey, TaxRates };

// ═══════════════════════════════════════════════════════════════════
// OPPORTUNITY SCHEMA
// ═══════════════════════════════════════════════════════════════════

export type TenderCadence = 'none' | 'annual' | 'semiannual';
export type LiquidityType = 'public' | 'ipo' | 'tender-only' | 'none';

export interface RetentionGrant {
  amount: number;
  startYr: number;
  vestYears: number;
}

export interface Opportunity {
  id: string;
  name: string;
  color: string;

  // Cash
  baseSalary: number;
  baseSalaryGrowthPct: number;
  signingBonus: number;
  targetBonusPct: number;

  // RSU
  rsuEnabled: boolean;
  rsuTotalValue: number;
  rsuVestYears: number;
  rsuCliffMonths: number;
  rsuVestFrequency: RsuVestFrequency;
  rsuDoubleTrigger: boolean;
  rsuAnnualRefresh: number;
  retentionGrant?: RetentionGrant;
  /** Optional per-year override (length = horizon). When set, overrides cliff/frequency/total. */
  customVestSchedule?: number[];

  // Options
  optionsEnabled: boolean;
  optionCount: number;
  strikePrice: number;
  currentFMV: number;
  optionVestYears: number;
  optionCliffMonths: number;
  /** Fraction of options granted as ISO (remainder is NSO). Clamped 0..1. */
  isoFraction: number;
  refreshAnnualShares: number;
  earlyExercise: boolean;
  qsbsEligible: boolean;
  dilutionEnabled: boolean;
  annualDilutionPct: number;
  cashlessExercise: boolean;

  // Existing vested equity at this opportunity (e.g. prior tenure)
  existingEquityValue: number;
  existingPoolSellFrac: number;

  // Liquidity
  liquidityType: LiquidityType;
  /** 1-based year index for IPO. 0 = none in horizon. */
  ipoYear: number;
  ipoLockupMonths: number;
  tenderEnabled: boolean;
  tenderFirstYear: number;
  tenderCadence: TenderCadence;
  tenderCapPct: number;
  tenderPriceFrac: number;

  // Donation matching
  donationMatchingEnabled: boolean;
  /** Fraction of grant donated (0..maxPct). Typically <= 0.25. */
  donationFrac: number;
  /** 1:1 match = 1.0, 2:1 = 2.0 */
  donationMatchRatio: number;
  donationMaxPct: number;

  /** Per-year valuation multipliers on grant-date value. Length = horizon. */
  valuationByYear: number[];
  /** Reference valuation at grant (in the same unit as valuationByYear, e.g. $B for company, $/share for share-based). */
  grantDateValuation: number;
}

export interface CalcSettings {
  timeHorizon: number;
  npvEnabled: boolean;
  npvDiscountRate: number;
  taxPreset: TaxPresetKey;
  filingStatus: FilingStatus;
  /** Used only when taxPreset === 'custom'. */
  customRates?: TaxRates;
}

// ═══════════════════════════════════════════════════════════════════
// VESTING SCHEDULE (opportunity-coupled resolver)
// `buildVestSchedule` itself lives in `equity-core.ts` (re-exported above);
// this resolver maps an Opportunity onto it.
// ═══════════════════════════════════════════════════════════════════

function resolveRsuSchedule(opp: Opportunity, horizon: number): number[] {
  if (opp.customVestSchedule && opp.customVestSchedule.length > 0) {
    const out = new Array(horizon).fill(0) as number[];
    for (let i = 0; i < horizon; i += 1) {
      out[i] = opp.customVestSchedule[i] ?? 0;
    }
    return out;
  }
  return buildVestSchedule(
    opp.rsuTotalValue,
    opp.rsuVestYears,
    opp.rsuCliffMonths,
    opp.rsuVestFrequency,
    horizon
  );
}

function retentionScheduleValBase(
  opp: Opportunity,
  horizon: number,
  valuationByYear: number[],
  valBaseRef: number
): number[] {
  const out = new Array(horizon).fill(0) as number[];
  const rg = opp.retentionGrant;
  if (!rg || rg.amount <= 0 || rg.vestYears <= 0) return out;
  const perYear = rg.amount / rg.vestYears;
  // Retention grant is priced at the opportunity's valuation in the year it starts.
  const startIdx = Math.max(0, rg.startYr - 1);
  const grantValRef = valuationByYear[startIdx] ?? valBaseRef;
  const grantMult = grantValRef / valBaseRef;
  // valBase is per-year vested USD at grant-date valuation.
  const valBasePer = grantMult === 0 ? 0 : perYear / grantMult;
  for (let i = startIdx; i < Math.min(horizon, startIdx + rg.vestYears); i += 1) {
    out[i] = valBasePer;
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════
// RESULT TYPES
// ═══════════════════════════════════════════════════════════════════

export interface TenderWindowResult {
  sharesSold: number;
  gross: number;
  strikeCostRecouped: number;
  ordTax: number;
  ltcgTax: number;
  qsbsExempt: number;
  netCash: number;
  nsoCashless: number;
  heldLtcgSold: number;
  isoDisqualified: number;
}

export interface YearResult {
  yr: number;
  /** Current-year valuation (same unit as Opportunity.grantDateValuation). */
  val: number;
  /** FMV per share this year (options opportunities). */
  fmv: number;
  /** Tender price per share this year (options opportunities). */
  tenderPrice: number;
  cashGross: number;
  cashNet: number;

  // RSU
  rsuVestedThisYrUSD: number;
  rsuVestedThisYrGrossUSD: number;
  rsuTaxUSD: number;
  ipoTaxEvent: number;

  // Options
  exerciseCost: number;
  vestTax: number;
  amtPref: number;
  paperVal: number;

  // Tender (options or pre-IPO RSU)
  tender: TenderWindowResult;

  // Post-IPO or existing pool sale
  poolSale: { proceeds: number; ltcgTax: number; net: number };

  // Existing equity pool (pre-existing vested holdings at this company)
  existingSale: { proceeds: number; ltcgTax: number; net: number };

  // Double-trigger pre-IPO paper value still locked up
  preIpoPaperValue: number;

  // Donation matching
  donatedUSD: number;
  charityUSD: number;
  donationTaxSaved: number;

  // Aggregates
  netCash: number;
  cumCash: number;
  heldVal: number;
  poolAfter: number;
  existingPoolAfter: number;
  pipeline: number;
  netWorth: number;

  // Share counts (options only)
  vestedUnexSharesNow: number;
  exercisedHeldSharesNow: number;
  sharesSold: number;
}

export interface OpportunityResult {
  id: string;
  years: YearResult[];
  meta: {
    y0EarlyExerciseCost: number;
    y0EarlyAmt: number;
    y0EarlyNSOTax: number;
    fmvPath: number[];
    valPath: number[];
    qsbsConsumed: number;
    donatedShares: number;
    matchedShares: number;
  };
}

// ═══════════════════════════════════════════════════════════════════
// MAIN BUILDER — generic; dispatches by feature flags
//
// Ordering inside each year (ported from reference):
//   1. Accrue cash income (net of cashEff).
//   2. Vest RSUs:
//      - doubleTrigger + pre-IPO → push into vestedUnrecognized (paper only)
//      - doubleTrigger + ipoYear → push + then revalue whole backlog as W-2
//      - otherwise (public-like) → treat as immediate W-2 + add lot to poolSell-able pool
//   3. Vest options:
//      - cashlessAllowed → push to vestedUnexercised (paper only)
//      - else exercise-and-hold → pay strike, pay vestTax on spread, push to exercisedHeld
//   4. Run tender windows (if firstTenderYr ≤ yr ≤ horizon/ipoYear):
//      - options: greedy order NSO-cashless → held-LTCG → ISO-cashless
//      - double-trigger RSU: scale vestedUnrecognized valBase by cap; ordinary tax
//   5. Post-IPO pool sell (public liquidityType from day 1, or post-ipoYear + lockup)
//   6. Existing equity pool sell (if existingPoolSellFrac > 0)
//   7. Roll up cumCash, heldVal, pipeline, netWorth.
// ═══════════════════════════════════════════════════════════════════

interface OptionLot {
  shares: number;
  type: 'iso' | 'nso';
  strike: number;
  grantFMV: number;
  vestedYr: number;
  basis?: number;
  acquiredYr?: number;
}

interface ExercisedLot {
  shares: number;
  basis: number;
  acquiredYr: number;
  type: 'iso' | 'nso';
}

interface UnrecognizedRsu {
  vestedYr: number;
  valBase: number;
}

interface RefreshGrantOpt {
  gYr: number;
  fmvAtGrant: number;
  strike: number;
  totalShares: number;
  vestPerYrR: number;
  isoPerYrR: number;
  nsoPerYrR: number;
}

function applyDilution(val: number, yr: number, dilPerYr: number): number {
  return val * Math.pow(1 - dilPerYr, yr);
}

export function buildOpportunity(opp: Opportunity, settings: CalcSettings): OpportunityResult {
  const TAX = resolveTaxRates(settings.taxPreset, settings.filingStatus, settings.customRates);
  const horizon = settings.timeHorizon;
  const valPath = new Array(horizon).fill(0).map((_, i) => opp.valuationByYear[i] ?? opp.grantDateValuation);

  // ── FMV path (options opportunities) ──
  const dilPerYr = opp.dilutionEnabled ? opp.annualDilutionPct : 0;
  const fmvBase = opp.currentFMV;
  const strike = opp.strikePrice;
  // Every equity figure on this page is a ratio against the grant-date valuation.
  // A zero, negative, or non-finite reference made fmvPath, poolTotal, poolSell
  // and the RSU pipeline divide by zero, so net worth came back as NaN and the
  // whole page rendered em-dashes. Fall back to 1, which reads valuationByYear
  // as plain multipliers — the same intent as the `mult` guard further down.
  const valBaseRef =
    Number.isFinite(opp.grantDateValuation) && opp.grantDateValuation > 0 ? opp.grantDateValuation : 1;
  const fmvPath = valPath.map((v, i) => applyDilution(fmvBase * (v / valBaseRef), i + 1, dilPerYr));
  const tenderPath = fmvPath.map((f) => f * (opp.tenderPriceFrac || 1));

  // ── Options vest split (ISO/NSO with $100k rule at grant-date FMV) ──
  const initShares = opp.optionCount;
  const optionVestYears = Math.max(1, opp.optionVestYears || 4);
  const vestPerYr = Math.floor(initShares / optionVestYears);
  const maxIsoPerYr = fmvBase > 0 ? Math.floor(ISO_100K_LIMIT / fmvBase) : vestPerYr;
  const requestedIsoPerYr = Math.floor(vestPerYr * Math.max(0, Math.min(1, opp.isoFraction)));
  const initIsoPerYr = Math.min(requestedIsoPerYr, maxIsoPerYr);
  const initNsoPerYr = vestPerYr - initIsoPerYr;

  // ── Option refresh tranches ──
  const refreshes: RefreshGrantOpt[] = [];
  if (opp.optionsEnabled && opp.refreshAnnualShares > 0) {
    for (let gYr = 1; gYr <= horizon; gYr += 1) {
      const fmvAtGrant = fmvPath[gYr - 1];
      const totalShares = opp.refreshAnnualShares;
      const vestPerYrR = Math.floor(totalShares / optionVestYears);
      const maxIsoR = fmvAtGrant > 0 ? Math.floor(ISO_100K_LIMIT / fmvAtGrant) : vestPerYrR;
      const reqIsoR = Math.floor(vestPerYrR * Math.max(0, Math.min(1, opp.isoFraction)));
      const isoPerYrR = Math.min(reqIsoR, maxIsoR);
      refreshes.push({
        gYr,
        fmvAtGrant,
        strike: fmvAtGrant,
        totalShares,
        vestPerYrR,
        isoPerYrR,
        nsoPerYrR: vestPerYrR - isoPerYrR
      });
    }
  }

  // ── State across years ──
  const vestedUnexercised: OptionLot[] = [];
  const exercisedHeld: ExercisedLot[] = [];
  const pool: PoolLot[] = [];
  const vestedUnrecognized: UnrecognizedRsu[] = [];
  const existingPool: PoolLot[] = [];
  if (opp.existingEquityValue > 0) {
    existingPool.push({
      id: 'existing',
      valBase: opp.existingEquityValue,
      basis: opp.existingEquityValue
    });
  }
  let cumCash = 0;
  let qsbsConsumed = 0;

  // ── Y0 early-exercise event for options (pre-Y1) ──
  let y0EarlyExerciseCost = 0;
  let y0EarlyAmt = 0;
  let y0EarlyNSOTax = 0;
  if (opp.optionsEnabled && opp.earlyExercise && initShares > 0) {
    y0EarlyExerciseCost = initShares * strike;
    const spread = Math.max(0, fmvBase - strike);
    const totalIsoShares = Math.min(initShares, optionVestYears * initIsoPerYr);
    const totalNsoShares = initShares - totalIsoShares;
    // AMT: whichever is higher between regular state+city path and federal AMT.
    y0EarlyAmt = totalIsoShares * spread * Math.max(TAX.isoStateCity, TAX.isoAmt);
    y0EarlyNSOTax = totalNsoShares * spread * TAX.ord;
    if (totalIsoShares > 0) {
      exercisedHeld.push({ shares: totalIsoShares, basis: fmvBase, acquiredYr: 0, type: 'iso' });
    }
    if (totalNsoShares > 0) {
      exercisedHeld.push({ shares: totalNsoShares, basis: fmvBase, acquiredYr: 0, type: 'nso' });
    }
    cumCash -= y0EarlyExerciseCost + y0EarlyAmt + y0EarlyNSOTax;
  }

  // ── RSU vest schedules ──
  const rsuSchedule = opp.rsuEnabled ? resolveRsuSchedule(opp, horizon) : new Array(horizon).fill(0);
  const retentionSchedule = opp.rsuEnabled
    ? retentionScheduleValBase(opp, horizon, valPath, valBaseRef)
    : new Array(horizon).fill(0);

  // ── Donation matching setup ──
  const donationFrac = opp.donationMatchingEnabled
    ? Math.max(0, Math.min(opp.donationMaxPct, opp.donationFrac))
    : 0;
  const matchRatio = opp.donationMatchingEnabled ? Math.max(0, opp.donationMatchRatio) : 0;
  const donatedShares = 0; // tracked only for RSU; shares equivalent is derived below
  const matchedShares = 0;

  // ── Tender cadence helper ──
  function windowsPerYear(yr: number): number {
    if (!opp.tenderEnabled) return 0;
    if (yr < opp.tenderFirstYear) return 0;
    if (opp.tenderCadence === 'none') return 0;
    if (opp.tenderCadence === 'semiannual') return 2;
    if (opp.tenderCadence === 'annual') return 1;
    return 0;
  }

  // ── Options tender window: greedy NSO-cashless → held-LTCG → ISO-cashless ──
  function runOptionsTenderWindow(
    yr: number,
    fmv: number,
    tenderPrice: number,
    capShares: number
  ): TenderWindowResult {
    const res: TenderWindowResult = {
      sharesSold: 0,
      gross: 0,
      strikeCostRecouped: 0,
      ordTax: 0,
      ltcgTax: 0,
      qsbsExempt: 0,
      netCash: 0,
      nsoCashless: 0,
      heldLtcgSold: 0,
      isoDisqualified: 0
    };
    if (capShares <= 0) return res;
    let remaining = capShares;

    // Step 1: NSO cashless from vestedUnexercised
    if (opp.cashlessExercise) {
      for (const lot of vestedUnexercised) {
        if (remaining <= 0) break;
        if (lot.type !== 'nso' || lot.shares <= 0) continue;
        const take = Math.min(lot.shares, remaining);
        const spread = Math.max(0, fmv - lot.strike);
        const capGain = Math.max(0, tenderPrice - fmv);
        const gross = take * tenderPrice;
        const strikeCost = take * lot.strike;
        const ordTaxOnSpread = take * spread * TAX.ord;
        const capGainTax = capGain * take * TAX.ltcg;
        res.gross += gross;
        res.strikeCostRecouped += strikeCost;
        res.ordTax += ordTaxOnSpread + capGainTax;
        res.sharesSold += take;
        res.nsoCashless += take;
        lot.shares -= take;
        remaining -= take;
      }
    }

    // Step 2: exercised-held shares with LTCG/QSBS treatment
    for (const lot of exercisedHeld) {
      if (remaining <= 0) break;
      if (lot.shares <= 0) continue;
      const take = Math.min(lot.shares, remaining);
      const yrsHeld = yr - lot.acquiredYr;
      const gain = take * Math.max(0, tenderPrice - lot.basis);
      const ltcgQualifies = yrsHeld >= 1;
      const qsbsQualifies =
        opp.qsbsEligible && opp.earlyExercise && lot.acquiredYr === 0 && yrsHeld >= 5;
      let lotExempt = 0;
      if (qsbsQualifies) {
        const remainingCap = QSBS_CAP - qsbsConsumed;
        lotExempt = Math.min(gain, Math.max(0, remainingCap));
        qsbsConsumed += lotExempt;
      }
      const taxable = gain - lotExempt;
      let lotTax;
      if (qsbsQualifies) {
        // Federal-only portion is exempt; state+city still applies on exempt amount.
        const stateCity = TAX.ltcg - 0.238;
        lotTax = lotExempt * Math.max(0, stateCity) + taxable * TAX.ltcg;
      } else if (ltcgQualifies) {
        lotTax = taxable * TAX.ltcg;
      } else {
        lotTax = taxable * TAX.ord;
      }
      res.gross += take * tenderPrice;
      res.ltcgTax += lotTax;
      res.qsbsExempt += lotExempt;
      res.sharesSold += take;
      res.heldLtcgSold += take;
      lot.shares -= take;
      remaining -= take;
    }

    // Step 3: ISO cashless as last resort (disqualifying disposition → ordinary on spread)
    if (opp.cashlessExercise && remaining > 0) {
      for (const lot of vestedUnexercised) {
        if (remaining <= 0) break;
        if (lot.type !== 'iso' || lot.shares <= 0) continue;
        const take = Math.min(lot.shares, remaining);
        const spread = Math.max(0, fmv - lot.strike);
        const gross = take * tenderPrice;
        const strikeCost = take * lot.strike;
        const ordTaxOnSpread = take * spread * TAX.ord;
        res.gross += gross;
        res.strikeCostRecouped += strikeCost;
        res.ordTax += ordTaxOnSpread;
        res.sharesSold += take;
        res.isoDisqualified += take;
        lot.shares -= take;
        remaining -= take;
      }
    }

    res.netCash = res.gross - res.strikeCostRecouped - res.ordTax - res.ltcgTax;
    return res;
  }

  const years: YearResult[] = [];

  for (let yr = 1; yr <= horizon; yr += 1) {
    const val = valPath[yr - 1];
    const fmv = fmvPath[yr - 1];
    const tenderPrice = tenderPath[yr - 1];
    const mult = valBaseRef === 0 ? 1 : val / valBaseRef;
    const tenderValue = val * (opp.tenderPriceFrac || 1);
    const tenderMult = valBaseRef === 0 ? 1 : tenderValue / valBaseRef;

    // ── 1. Cash comp ──
    const cashGrossBase = opp.baseSalary * Math.pow(1 + opp.baseSalaryGrowthPct, yr - 1);
    const bonus = cashGrossBase * opp.targetBonusPct;
    const signingThisYr = yr === 1 ? opp.signingBonus : 0;
    const cashGross = cashGrossBase + bonus + signingThisYr;
    const cashNet = cashGross * (1 - TAX.cashEff);

    // ── 2. RSU vest ──
    //
    // All vest tracking is done in **grant-date dollars (valBase)**. RSUs
    // represent a fixed share count, so a grant of $X at grantDateValuation V
    // is worth X × (curVal / V) in year-yr dollars. We only multiply by `mult`
    // at the moment of recognition (W-2 event, pool basis), and `valBase`
    // stays in grant-date dollars so `poolTotal(pool, curVal, V)` revalues it
    // correctly across years.
    //
    // Bug history: an earlier version treated `rsuSchedule[yr-1]` as year-yr
    // dollars and divided by mult to derive valBase, which double-deflated
    // the initial grant and broke RSU appreciation entirely. The
    // run-job-offer-tests suite locks in the corrected scaling.
    let rsuTaxUSD = 0;
    let ipoTaxEvent = 0;
    let initVestValBase = 0;       // grant-date $ vested this yr from initial grant (post-donation)
    let initVestUSD = 0;           // year-yr $ value of initVestValBase
    let refreshVestValBase = 0;    // grant-date $ vested this yr from annual refreshes
    let refreshVestUSD = 0;        // year-yr $ value of refreshVestValBase
    let retentionVestValBase = 0;  // grant-date $ vested this yr from retention grant
    let retentionVestUSD = 0;      // year-yr $ value of retentionVestValBase
    let initVestDonatedUSD = 0;
    let charityThisYrUSD = 0;
    let donationTaxSavedThisYr = 0;

    if (opp.rsuEnabled) {
      const initVestValBaseGross = rsuSchedule[yr - 1] ?? 0;
      const donatedValBase = initVestValBaseGross * donationFrac;
      initVestValBase = initVestValBaseGross - donatedValBase;
      initVestUSD = initVestValBase * mult;
      initVestDonatedUSD = donatedValBase * mult;

      for (let g = 1; g < yr; g += 1) {
        const vYrFromGrant = yr - g;
        if (vYrFromGrant >= 1 && vYrFromGrant <= opp.rsuVestYears) {
          const grantPrice = valPath[g - 1];
          // Refresh granted at year g of `annualRefresh` value (year-g $ at val=grantPrice),
          // vesting evenly over rsuVestYears. Convert per-year vest to grant-date $:
          //   year-g $ per vest = annualRefresh / vestYears
          //   grant-date $ per vest = (annualRefresh / vestYears) × (valBaseRef / grantPrice)
          const valBasePer =
            grantPrice === 0
              ? 0
              : (opp.rsuAnnualRefresh / Math.max(1, opp.rsuVestYears)) * (valBaseRef / grantPrice);
          refreshVestValBase += valBasePer;
        }
      }
      refreshVestUSD = refreshVestValBase * mult;

      retentionVestValBase = retentionSchedule[yr - 1] ?? 0;
      retentionVestUSD = retentionVestValBase * mult;

      const totalNetValBase = initVestValBase + refreshVestValBase + retentionVestValBase;
      const totalVestThisYrNet = totalNetValBase * mult; // year-yr $ at recognition

      if (opp.donationMatchingEnabled && initVestDonatedUSD > 0) {
        charityThisYrUSD = initVestDonatedUSD * (1 + matchRatio);
        donationTaxSavedThisYr = initVestDonatedUSD * TAX.ord;
      }

      const isIpoYear = opp.liquidityType === 'ipo' && opp.ipoYear === yr;
      const preIpo = opp.liquidityType === 'ipo' && yr < opp.ipoYear;
      const postIpo = opp.liquidityType === 'ipo' && yr > opp.ipoYear;

      if (!opp.rsuDoubleTrigger) {
        // Public-like: immediate W-2 on net (post-donation) vest, lot joins sellable pool.
        rsuTaxUSD = totalVestThisYrNet * TAX.ord;
        if (totalNetValBase > 0) {
          pool.push({ id: `rsu-y${yr}`, valBase: totalNetValBase, basis: totalVestThisYrNet });
        }
      } else if (postIpo || opp.liquidityType === 'public' || opp.liquidityType === 'none') {
        // Already past IPO OR treated as public: immediate W-2, into pool.
        rsuTaxUSD = totalVestThisYrNet * TAX.ord;
        if (totalNetValBase > 0) {
          pool.push({ id: `rsu-y${yr}`, valBase: totalNetValBase, basis: totalVestThisYrNet });
        }
      } else if (isIpoYear) {
        // Push this-year vest into unrecognized, then revalue entire backlog as W-2.
        if (totalNetValBase > 0) {
          vestedUnrecognized.push({ vestedYr: yr, valBase: totalNetValBase });
        }
        const backlogValBase = vestedUnrecognized.reduce((s, v) => s + v.valBase, 0);
        const backlogAtIpo = backlogValBase * mult;
        ipoTaxEvent = backlogAtIpo;
        rsuTaxUSD = ipoTaxEvent * TAX.ord;
        if (backlogValBase > 0) {
          pool.push({ id: `ipo-backlog-y${yr}`, valBase: backlogValBase, basis: backlogAtIpo });
        }
        vestedUnrecognized.length = 0;
      } else if (preIpo || opp.liquidityType === 'tender-only') {
        // Pre-IPO or tender-only: paper vest into unrecognized pool.
        if (totalNetValBase > 0) {
          vestedUnrecognized.push({ vestedYr: yr, valBase: totalNetValBase });
        }
      }
    }

    // ── 3. Options vest ──
    let exerciseCost = 0;
    let vestTax = 0;
    let amtPref = 0;
    let paperVal = 0;

    if (opp.optionsEnabled && !opp.earlyExercise) {
      if (opp.cashlessExercise) {
        vestedUnexercised.push({
          shares: initIsoPerYr,
          type: 'iso',
          strike,
          grantFMV: fmvBase,
          vestedYr: yr
        });
        vestedUnexercised.push({
          shares: initNsoPerYr,
          type: 'nso',
          strike,
          grantFMV: fmvBase,
          vestedYr: yr
        });
        paperVal += vestPerYr * fmv;
      } else {
        paperVal += vestPerYr * fmv;
        const spread = Math.max(0, fmv - strike);
        vestTax += initNsoPerYr * spread * TAX.ord;
        vestTax += initIsoPerYr * spread * TAX.isoStateCity;
        amtPref += initIsoPerYr * spread;
        exerciseCost += vestPerYr * strike;
        exercisedHeld.push({ shares: initIsoPerYr, basis: fmv, acquiredYr: yr, type: 'iso' });
        exercisedHeld.push({ shares: initNsoPerYr, basis: fmv, acquiredYr: yr, type: 'nso' });
      }
      for (const r of refreshes) {
        if (yr <= r.gYr) continue;
        if (yr > r.gYr + optionVestYears) continue;
        paperVal += r.vestPerYrR * fmv;
        if (opp.cashlessExercise) {
          vestedUnexercised.push({
            shares: r.isoPerYrR,
            type: 'iso',
            strike: r.strike,
            grantFMV: r.fmvAtGrant,
            vestedYr: yr
          });
          vestedUnexercised.push({
            shares: r.nsoPerYrR,
            type: 'nso',
            strike: r.strike,
            grantFMV: r.fmvAtGrant,
            vestedYr: yr
          });
        } else {
          const spread = Math.max(0, fmv - r.strike);
          vestTax += r.nsoPerYrR * spread * TAX.ord;
          vestTax += r.isoPerYrR * spread * TAX.isoStateCity;
          amtPref += r.isoPerYrR * spread;
          exerciseCost += r.vestPerYrR * r.strike;
          exercisedHeld.push({ shares: r.isoPerYrR, basis: fmv, acquiredYr: yr, type: 'iso' });
          exercisedHeld.push({ shares: r.nsoPerYrR, basis: fmv, acquiredYr: yr, type: 'nso' });
        }
      }
    }

    // ── 4. Tender windows ──
    let tender: TenderWindowResult = {
      sharesSold: 0,
      gross: 0,
      strikeCostRecouped: 0,
      ordTax: 0,
      ltcgTax: 0,
      qsbsExempt: 0,
      netCash: 0,
      nsoCashless: 0,
      heldLtcgSold: 0,
      isoDisqualified: 0
    };

    if (opp.optionsEnabled) {
      const winCount = windowsPerYear(yr);
      if (winCount > 0) {
        for (let w = 0; w < winCount; w += 1) {
          const elNow =
            vestedUnexercised.reduce((s, l) => s + l.shares, 0) +
            exercisedHeld.reduce((s, l) => s + l.shares, 0);
          const capNow = Math.floor(elNow * opp.tenderCapPct);
          const wr = runOptionsTenderWindow(yr, fmv, tenderPrice, capNow);
          tender.sharesSold += wr.sharesSold;
          tender.gross += wr.gross;
          tender.strikeCostRecouped += wr.strikeCostRecouped;
          tender.ordTax += wr.ordTax;
          tender.ltcgTax += wr.ltcgTax;
          tender.qsbsExempt += wr.qsbsExempt;
          tender.netCash += wr.netCash;
          tender.nsoCashless += wr.nsoCashless;
          tender.heldLtcgSold += wr.heldLtcgSold;
          tender.isoDisqualified += wr.isoDisqualified;
        }
      }
    } else if (opp.rsuEnabled && opp.rsuDoubleTrigger && opp.liquidityType === 'ipo') {
      // Pre-IPO RSU tender: scale vestedUnrecognized valBase by cap; ordinary-income tax.
      const preIpoActive = yr < opp.ipoYear && yr >= opp.tenderFirstYear;
      const winCount = preIpoActive ? windowsPerYear(yr) : 0;
      for (let w = 0; w < winCount; w += 1) {
        const vestedValBaseNow = vestedUnrecognized.reduce((s, v) => s + v.valBase, 0);
        const valBaseToTender = vestedValBaseNow * opp.tenderCapPct;
        if (valBaseToTender <= 0) break;
        const tenderProceedsGross = valBaseToTender * tenderMult;
        const taxOnTender = tenderProceedsGross * TAX.ord;
        const tenderNetCash = tenderProceedsGross - taxOnTender;
        const scale = valBaseToTender / vestedValBaseNow;
        for (const v of vestedUnrecognized) {
          v.valBase *= 1 - scale;
        }
        tender.gross += tenderProceedsGross;
        tender.ordTax += taxOnTender;
        tender.netCash += tenderNetCash;
      }
    } else if (opp.rsuEnabled && opp.rsuDoubleTrigger && opp.liquidityType === 'tender-only') {
      // Tender-only RSU (no IPO planned): same mechanic, no horizon gate.
      const winCount = windowsPerYear(yr);
      for (let w = 0; w < winCount; w += 1) {
        const vestedValBaseNow = vestedUnrecognized.reduce((s, v) => s + v.valBase, 0);
        const valBaseToTender = vestedValBaseNow * opp.tenderCapPct;
        if (valBaseToTender <= 0) break;
        const tenderProceedsGross = valBaseToTender * tenderMult;
        const taxOnTender = tenderProceedsGross * TAX.ord;
        const tenderNetCash = tenderProceedsGross - taxOnTender;
        const scale = valBaseToTender / vestedValBaseNow;
        for (const v of vestedUnrecognized) {
          v.valBase *= 1 - scale;
        }
        tender.gross += tenderProceedsGross;
        tender.ordTax += taxOnTender;
        tender.netCash += tenderNetCash;
      }
    }

    // ── 5. Post-IPO / public pool sell ──
    let poolSale = { proceeds: 0, ltcgTax: 0, net: 0 };
    const canSellPool =
      opp.liquidityType === 'public' ||
      (opp.liquidityType === 'ipo' && yr > opp.ipoYear);
    if (canSellPool && pool.length > 0 && opp.existingPoolSellFrac > 0) {
      // Reuse existingPoolSellFrac as the post-liquidity sell cadence (matches reference's
      // anthSellFrac/dbxSellFrac semantics — user tweaks one control for "how much to sell").
      const { pool: nextPool, proceeds, ltcgTax, net } = poolSell(
        pool,
        opp.existingPoolSellFrac,
        val,
        valBaseRef,
        TAX.ltcg
      );
      poolSale = { proceeds, ltcgTax, net };
      pool.length = 0;
      nextPool.forEach((l) => pool.push(l));
    }

    // ── 6. Existing equity pool sell ──
    let existingSale = { proceeds: 0, ltcgTax: 0, net: 0 };
    if (existingPool.length > 0 && opp.existingPoolSellFrac > 0) {
      const { pool: nextEx, proceeds, ltcgTax, net } = poolSell(
        existingPool,
        opp.existingPoolSellFrac,
        val,
        valBaseRef,
        TAX.ltcg
      );
      existingSale = { proceeds, ltcgTax, net };
      existingPool.length = 0;
      nextEx.forEach((l) => existingPool.push(l));
    }

    // ── 7. Aggregates ──
    const netCash =
      cashNet - exerciseCost - vestTax - rsuTaxUSD + tender.netCash + poolSale.net + existingSale.net;
    cumCash += netCash;

    const vestedUnexSharesNow = vestedUnexercised.reduce((s, l) => s + l.shares, 0);
    const exercisedHeldSharesNow = exercisedHeld.reduce((s, l) => s + l.shares, 0);
    const exercisedHeldValue = exercisedHeldSharesNow * fmv;
    const vestedUnexIntrinsic = vestedUnexercised.reduce(
      (s, l) => s + Math.max(0, fmv - l.strike) * l.shares,
      0
    );
    const heldVal = exercisedHeldValue + vestedUnexIntrinsic;

    const preIpoPaperValue = vestedUnrecognized.reduce((s, v) => s + v.valBase * mult, 0);
    const poolAfter = poolTotal(pool, val, valBaseRef);
    const existingPoolAfter = poolTotal(existingPool, val, valBaseRef);

    // Pipeline: future in-horizon value not yet vested (rough unvested estimate)
    let pipeline = 0;
    if (opp.optionsEnabled && !opp.earlyExercise) {
      for (let fy = yr + 1; fy <= horizon; fy += 1) {
        pipeline += vestPerYr * Math.max(0, fmvPath[fy - 1] - strike);
      }
      for (const r of refreshes) {
        for (let fy = yr + 1; fy <= Math.min(r.gYr + optionVestYears, horizon); fy += 1) {
          if (fy > r.gYr) pipeline += r.vestPerYrR * Math.max(0, fmvPath[fy - 1] - r.strike);
        }
      }
    }
    if (opp.rsuEnabled) {
      for (let fy = yr + 1; fy <= horizon; fy += 1) {
        const futureMult = valPath[fy - 1] / valBaseRef;
        // Initial grant vest in year fy (grant-date $) → year-fy $ via futureMult.
        pipeline += (rsuSchedule[fy - 1] ?? 0) * futureMult;
        // Refresh vest in year fy: only refreshes granted at year g where g+1 ≤ fy ≤ g+vestYears.
        for (let g = 1; g < fy; g += 1) {
          if (fy - g > opp.rsuVestYears) continue;
          const grantPrice = valPath[g - 1];
          const refreshVpy =
            grantPrice === 0
              ? 0
              : (opp.rsuAnnualRefresh / Math.max(1, opp.rsuVestYears)) * (valPath[fy - 1] / grantPrice);
          pipeline += refreshVpy;
        }
        pipeline += (retentionSchedule[fy - 1] ?? 0) * futureMult;
      }
    }

    const netWorth = cumCash + heldVal + poolAfter + existingPoolAfter + preIpoPaperValue;

    years.push({
      yr,
      val,
      fmv,
      tenderPrice,
      cashGross,
      cashNet,
      rsuVestedThisYrUSD: initVestUSD + refreshVestUSD + retentionVestUSD,
      rsuVestedThisYrGrossUSD:
        ((rsuSchedule[yr - 1] ?? 0) + refreshVestValBase + retentionVestValBase) * mult,
      rsuTaxUSD,
      ipoTaxEvent,
      exerciseCost,
      vestTax,
      amtPref,
      paperVal,
      tender,
      poolSale,
      existingSale,
      preIpoPaperValue,
      donatedUSD: initVestDonatedUSD,
      charityUSD: charityThisYrUSD,
      donationTaxSaved: donationTaxSavedThisYr,
      netCash,
      cumCash,
      heldVal,
      poolAfter,
      existingPoolAfter,
      pipeline,
      netWorth,
      vestedUnexSharesNow,
      exercisedHeldSharesNow,
      sharesSold: tender.sharesSold
    });
  }

  return {
    id: opp.id,
    years,
    meta: {
      y0EarlyExerciseCost,
      y0EarlyAmt,
      y0EarlyNSOTax,
      fmvPath,
      valPath,
      qsbsConsumed,
      donatedShares,
      matchedShares
    }
  };
}

// ═══════════════════════════════════════════════════════════════════
// NPV — midpoint convention (ported from reference)
// ═══════════════════════════════════════════════════════════════════

export function applyDiscount<T>(years: T[], rate: number, getter: (y: T) => number): number {
  let sum = 0;
  years.forEach((y, i) => {
    const t = i + 0.5;
    sum += getter(y) / Math.pow(1 + rate, t);
  });
  return sum;
}

// ═══════════════════════════════════════════════════════════════════
// MULTI-OPP ORCHESTRATION
// ═══════════════════════════════════════════════════════════════════

export function buildAll(opps: Opportunity[], settings: CalcSettings): OpportunityResult[] {
  return opps.map((opp) => buildOpportunity(opp, settings));
}

// ═══════════════════════════════════════════════════════════════════
// BREAKEVEN SWEEP
// Scale sweepOpp's valuation path proportionally at ~50 points between
// 0.1× and 10× its final-year value, keeping the earlier-year ratios.
// Returns sweep-opp NW at each point; baseline NW is a scalar (unswept).
// ═══════════════════════════════════════════════════════════════════

export interface SweepPoint {
  finalVal: number;
  sweepNetWorth: number;
  sweepCumCash: number;
}

export function computeBreakevenSweep(
  sweepOpp: Opportunity,
  settings: CalcSettings,
  steps = 50,
  minMult = 0.1,
  maxMult = 10
): SweepPoint[] {
  const points: SweepPoint[] = [];
  const horizon = settings.timeHorizon;
  const base = sweepOpp.valuationByYear.slice(0, horizon);
  const finalBase = base[horizon - 1] || 1;
  for (let i = 0; i <= steps; i += 1) {
    const mult = minMult + ((maxMult - minMult) * i) / steps;
    const finalVal = finalBase * mult;
    const scaled = base.map((v, idx) => {
      if (idx === horizon - 1) return finalVal;
      const lerp = idx / Math.max(1, horizon - 1);
      return v * (1 + (mult - 1) * lerp);
    });
    const result = buildOpportunity({ ...sweepOpp, valuationByYear: scaled }, settings);
    const last = result.years[horizon - 1];
    points.push({ finalVal, sweepNetWorth: last?.netWorth ?? 0, sweepCumCash: last?.cumCash ?? 0 });
  }
  return points;
}

// ═══════════════════════════════════════════════════════════════════
// SENSITIVITY — bear/base/bull multipliers with probabilities
// ═══════════════════════════════════════════════════════════════════

export interface SensitivityInput {
  bearMult: number;
  bullMult: number;
  probBear: number;
  probBase: number;
  probBull: number;
}

export interface SensitivityResult {
  bearNW: number;
  baseNW: number;
  bullNW: number;
  expectedNW: number;
}

export function computeSensitivity(
  opp: Opportunity,
  settings: CalcSettings,
  input: SensitivityInput
): SensitivityResult {
  const horizon = settings.timeHorizon;
  const base = opp.valuationByYear.slice(0, horizon);
  const scale = (mult: number) => base.map((v) => v * mult);
  const bear = buildOpportunity({ ...opp, valuationByYear: scale(input.bearMult) }, settings);
  const baseR = buildOpportunity({ ...opp, valuationByYear: base }, settings);
  const bull = buildOpportunity({ ...opp, valuationByYear: scale(input.bullMult) }, settings);
  const bearNW = bear.years[horizon - 1]?.netWorth ?? 0;
  const baseNW = baseR.years[horizon - 1]?.netWorth ?? 0;
  const bullNW = bull.years[horizon - 1]?.netWorth ?? 0;
  const total = input.probBear + input.probBase + input.probBull;
  const safeTotal = total <= 0 ? 1 : total;
  const expectedNW =
    (bearNW * input.probBear + baseNW * input.probBase + bullNW * input.probBull) / safeTotal;
  return { bearNW, baseNW, bullNW, expectedNW };
}

// Formatters (`fmt`, `fmtN`, `pctFmt`) live in `equity-core.ts` and are
// re-exported from the top of this module.
